import { useState } from 'react';
import { toastValidation } from '@/lib/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import type { TicketAction } from '@/lib/api';
import { useTicketAction } from '@/lib/use-ticket-action';
import { AssigneePicker } from '@/components/AssigneePicker';
import { useAccess } from '@/lib/use-access';
import {
  Hand,
  CheckCircle2,
  AlertOctagon,
  UserPlus,
  Clock,
  Archive,
  Ban,
  X,
} from 'lucide-react';

// Floating action bar that appears at the bottom-center of the viewport when
// the dashboard has ≥1 ticket selected. Each button fans out N parallel
// postAction calls (one per selected id) via Promise.allSettled.

type SimpleAction = 'claim' | 'resolve' | 'escalate' | 'snooze_1h' | 'archive' | 'close';

export function BulkActionsBar({
  selectedIds,
  selectedInboxIds,
  onClear,
  onRetainFailed,
}: {
  selectedIds: string[];
  /** The inbox of each selected ticket (any order). Drives which actions are
   *  offered: a button shows only when the role allows it in EVERY inbox. */
  selectedInboxIds?: Array<string | null | undefined>;
  onClear: () => void;
  /** Narrow the selection down to just the ids that failed, so the user can
   *  retry exactly those. Falls back to clearing when not supplied. */
  onRetainFailed?: (ids: string[]) => void;
}) {
  // Per-inbox permissions (migration 078). The dispatcher refuses per ticket
  // anyway; this stops offering actions the role can't perform.
  const access = useAccess();
  const inAll = (perm: string) =>
    selectedInboxIds && selectedInboxIds.length > 0
      ? selectedInboxIds.every((id) => access.canIn(id, perm))
      : access.canAny(perm);
  const canClaim = inAll('tickets.claim');
  const canStatus = inAll('tickets.status');
  const canAssignOthers = inAll('tickets.assign_others');
  const canArchive = inAll('tickets.archive');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');

  // Every selected ticket changes on screen at once and the requests go out
  // four at a time in the background (lib/use-ticket-action.ts keeps the
  // n8n Starter-plan concurrency cap). The selection clears straight away;
  // tickets the server refuses are put back and re-selected so the warning
  // toast's "press the same action again" is actionable.
  const { runMany } = useTicketAction();
  const fanOut = async (action: TicketAction, payload?: Record<string, unknown>) => {
    const ids = selectedIds;
    onClear();
    const { failedIds } = await runMany(ids, action, payload);
    if (failedIds.length > 0) onRetainFailed?.(failedIds);
  };

  // Destructive fan-outs get a confirmation once the blast radius is more than
  // a handful of tickets. Claim / Snooze / Reassign are reversible in one click
  // and stay immediate — gating everything would just train people to dismiss
  // the dialog without reading it.
  const DESTRUCTIVE: Partial<Record<SimpleAction, { verb: string; consequence: string }>> = {
    resolve: {
      verb: 'Resolve',
      consequence: 'They leave the open queue and their SLA clocks stop.',
    },
    archive: {
      verb: 'Archive',
      consequence: 'They disappear from the dashboard. You can restore them from the Archived tab.',
    },
    close: {
      verb: 'Dismiss',
      consequence:
        'This marks them as marketing / spam / no-action and hides them. Use Resolve for legitimate tickets you have finished.',
    },
  };
  const CONFIRM_THRESHOLD = 5;

  const [pendingAction, setPendingAction] = useState<SimpleAction | null>(null);

  const fire = (action: SimpleAction) => {
    const danger = DESTRUCTIVE[action];
    if (danger && selectedIds.length > CONFIRM_THRESHOLD) {
      setPendingAction(action);
      return;
    }
    void fanOut(action);
  };

  const submitReassign = () => {
    const to = reassignTo.trim();
    if (!to) return toastValidation('Pick a teammate to reassign to');
    setReassignOpen(false);
    setReassignTo('');
    void fanOut('reassign', { reassign_to: to });
  };

  const count = selectedIds.length;

  return (
    <>
      <div
        // Fixed at the bottom of the viewport, centred horizontally.
        // Mobile: full-width minus padding, scrollable horizontally if buttons overflow.
        className="fixed bottom-3 left-1/2 z-40 max-w-[calc(100vw-1.5rem)] -translate-x-1/2 overflow-x-auto rounded-full border border-border bg-background px-2 py-1.5 shadow-lg"
        // `group`, not `toolbar` — the toolbar role promises arrow-key roving
        // between controls, which isn't implemented here.
        role="group"
        aria-label={`Bulk actions for ${count} selected ticket${count === 1 ? '' : 's'}`}
      >
        <div className="flex items-center gap-1 whitespace-nowrap">
          <span className="px-2 text-xs font-semibold tabular-nums">
            {count} selected
          </span>
          <Separator />
          {canClaim ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fire('claim')}
            className="h-8 px-2.5 text-xs"
          >
            <Hand className="mr-1 h-3.5 w-3.5" />
            Claim
          </Button>
          ) : null}
          {canStatus ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fire('resolve')}
            className="h-8 px-2.5 text-xs hover:text-tint-success-fg"
          >
            <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            Resolve
          </Button>
          ) : null}
          {canStatus ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fire('escalate')}
            className="h-8 px-2.5 text-xs hover:text-tint-critical-fg"
          >
            <AlertOctagon className="mr-1 h-3.5 w-3.5" />
            Escalate
          </Button>
          ) : null}
          {canAssignOthers ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setReassignOpen(true)}
            className="h-8 px-2.5 text-xs"
          >
            <UserPlus className="mr-1 h-3.5 w-3.5" />
            Reassign
          </Button>
          ) : null}
          {canStatus ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fire('snooze_1h')}
            className="h-8 px-2.5 text-xs"
          >
            <Clock className="mr-1 h-3.5 w-3.5" />
            Snooze 1h
          </Button>
          ) : null}
          {canArchive ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fire('archive')}
            className="h-8 px-2.5 text-xs"
            title="Hide selected from dashboard (Archived tab to restore)"
          >
            <Archive className="mr-1 h-3.5 w-3.5" />
            Archive
          </Button>
          ) : null}
          {canStatus ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => fire('close')}
            className="h-8 px-2.5 text-xs hover:text-tint-critical-fg"
            title="Dismiss as marketing / spam / no action — archives and hides. Use Resolve for legitimate tickets you've finished."
          >
            <Ban className="mr-1 h-3.5 w-3.5" />
            Dismiss
          </Button>
          ) : null}
          {!canClaim && !canStatus && !canAssignOthers && !canArchive && !access.isLoading ? (
            <span className="px-2 text-2xs text-muted-foreground">
              Your role can't act on {count === 1 ? 'this ticket' : 'these tickets'}
            </span>
          ) : null}
          <Separator />
          <Button
            size="sm"
            variant="ghost"
            onClick={onClear}
            className="h-8 px-2 text-xs text-muted-foreground"
            aria-label="Clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={pendingAction !== null}
        onOpenChange={(next) => {
          if (!next) setPendingAction(null);
        }}
        severity="destructive"
        title={
          pendingAction
            ? `${DESTRUCTIVE[pendingAction]?.verb} ${count} ticket${count === 1 ? '' : 's'}?`
            : ''
        }
        description={pendingAction ? DESTRUCTIVE[pendingAction]?.consequence : ''}
        confirmLabel={
          pendingAction
            ? `${DESTRUCTIVE[pendingAction]?.verb} ${count}`
            : 'Confirm'
        }
        onConfirm={() => {
          const action = pendingAction;
          setPendingAction(null);
          if (action) void fanOut(action);
        }}
      />

      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-h-[90vh] max-w-[95vw] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Reassign {count} ticket{count === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>
              Pick a teammate to take over. Each ticket fires its own
              dispatcher action — partial failures will toast a warning.
            </DialogDescription>
          </DialogHeader>
          <AssigneePicker
            value={reassignTo || null}
            onChange={(id) => setReassignTo(id)}
            label="Reassign to"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReassignOpen(false)}>Cancel</Button>
            <Button onClick={submitReassign} disabled={!reassignTo.trim()}>
              Reassign all
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Separator() {
  return <span aria-hidden className="h-5 w-px bg-border" />;
}
